<template>
	<div>
		<p class="small text-body-secondary mb-3">
			{{ technicianLabel(route.technicianId, users) }} ·
			{{ routeWindowText(route) }}
			<template v-if="route.dispatchedAt">
				· dispatched {{ formatDate(new Date(route.dispatchedAt)) }}
			</template>
			<template v-if="route.completedAt">
				· completed {{ formatDate(new Date(route.completedAt)) }}
			</template>
		</p>

		<div class="d-flex flex-wrap gap-2 mb-3">
			<button
				v-if="route.state === 'draft'"
				type="button"
				class="btn btn-sm btn-primary"
				:disabled="busy"
				@click="dispatchRoute"
			>
				<i class="bi bi-send me-1"></i>Dispatch
			</button>
			<span
				v-if="!editable"
				class="small text-body-secondary align-self-center"
			>
				This Route is {{ route.state }}; §8 Rule 6 keeps its membership and
				sequence as the historical record.
			</span>
		</div>

		<div v-if="editable" class="input-group input-group-sm mb-3">
			<select
				v-model.number="addAssignmentId"
				class="form-select"
				aria-label="Assignment to add"
				:disabled="busy"
			>
				<option :value="0" disabled>
					Add one of this technician's draft assignments…
				</option>
				<option
					v-for="assignment in addable"
					:key="assignment.id"
					:value="assignment.id"
				>
					{{ assignmentLabel(assignment) }}
				</option>
			</select>
			<button
				type="button"
				class="btn btn-outline-primary"
				:disabled="busy || addAssignmentId <= 0"
				@click="addMember"
			>
				Add
			</button>
		</div>

		<div class="row g-3">
			<div class="col-12 col-lg-7">
				<h3 class="h6">Sequence</h3>
				<p v-if="ordered.length === 0" class="small text-body-secondary mb-2">
					No member holds a position yet — drop a row here to sequence it.
				</p>
				<ol
					v-else
					class="list-group list-group-numbered mb-2"
					@dragover.prevent
					@drop.prevent="dropAt(ordered.length)"
				>
					<li
						v-for="(member, index) in ordered"
						:key="member.assignmentId"
						class="list-group-item d-flex justify-content-between align-items-center py-1"
						:draggable="editable"
						@dragstart="dragId = member.assignmentId"
						@dragend="dragId = null"
						@dragover.prevent
						@drop.prevent.stop="dropAt(index)"
					>
						<button
							type="button"
							class="btn btn-link btn-sm p-0 text-start text-truncate"
							@click="
								emit('select', { kind: 'assignment', id: member.assignmentId })
							"
						>
							{{ memberLabel(member.assignmentId) }}
						</button>
						<button
							v-if="editable"
							type="button"
							class="btn btn-outline-danger btn-sm"
							:disabled="busy"
							title="Remove from route"
							@click="removeMember(member.assignmentId)"
						>
							<i class="bi bi-x-lg"></i>
						</button>
					</li>
				</ol>
				<p v-if="editable && ordered.length > 0" class="form-text">
					Drag a row onto another to state the order; the whole sequence is
					written in one call.
				</p>
			</div>

			<div class="col-12 col-lg-5">
				<h3 class="h6">Unsequenced</h3>
				<p
					v-if="unsequenced.length === 0"
					class="small text-body-secondary mb-2"
				>
					Every member holds a position.
				</p>
				<ul v-else class="list-group list-group-flush mb-2">
					<li
						v-for="member in unsequenced"
						:key="member.assignmentId"
						class="list-group-item d-flex justify-content-between align-items-center py-1 px-0"
						:draggable="editable"
						@dragstart="dragId = member.assignmentId"
						@dragend="dragId = null"
					>
						<button
							type="button"
							class="btn btn-link btn-sm p-0 text-start text-truncate"
							@click="
								emit('select', { kind: 'assignment', id: member.assignmentId })
							"
						>
							{{ memberLabel(member.assignmentId) }}
						</button>
						<button
							v-if="editable"
							type="button"
							class="btn btn-outline-danger btn-sm"
							:disabled="busy"
							title="Remove from route"
							@click="removeMember(member.assignmentId)"
						>
							<i class="bi bi-x-lg"></i>
						</button>
					</li>
				</ul>
				<p v-if="editable && unsequenced.length > 0" class="form-text">
					Drag an unsequenced member onto the sequence to place it.
				</p>
			</div>
		</div>

		<div v-if="routeError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ routeError }}
		</div>

		<div v-if="route.state === 'draft'" class="mt-3 pt-3 border-top">
			<button
				v-if="!discardPending"
				type="button"
				class="btn btn-sm btn-outline-danger w-100"
				:disabled="busy"
				@click="discardPending = true"
			>
				<i class="bi bi-trash3 me-1"></i>Discard route
			</button>
			<div v-else>
				<p class="small mb-2">
					Discard route #{{ route.id }}?
					<template v-if="route.members.length > 0">
						Its {{ route.members.length }}
						{{
							route.members.length === 1
								? "assignment returns"
								: "assignments return"
						}}
						to the unassigned pool.
					</template>
					<template v-else>It holds no work.</template>
				</p>
				<div class="d-flex gap-2">
					<button
						type="button"
						class="btn btn-sm btn-danger"
						:disabled="busy"
						@click="discardRoute"
					>
						Discard route
					</button>
					<button
						type="button"
						class="btn btn-sm btn-outline-secondary"
						:disabled="busy"
						@click="discardPending = false"
					>
						Keep route
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import { formatDate } from "@/format";
import type {
	MapReviewSiteRow,
	RouteReorderRequest,
	RouteStateUpdate,
	SyncAssignment,
	SyncRoute,
	User,
} from "@/type/api";

import { orderedMembers, unsequencedMembers, type Selection } from "./model";
import {
	describeError,
	kindLabel,
	routeWindowText,
	targetLabel,
	technicianLabel,
} from "./wire";

const props = defineProps<{
	route: SyncRoute;
	users: User[];
	sites: MapReviewSiteRow[];
	assignments: SyncAssignment[];
}>();

const emit = defineEmits<{
	notify: [title: string, message: string];
	changed: [];
	select: [selection: Selection];
}>();

const busy = ref(false);
const routeError = ref("");
const addAssignmentId = ref(0);
const dragId = ref<number | null>(null);
const discardPending = ref(false);

const assignmentIndex = computed(
	() => new Map(props.assignments.map((row) => [row.id, row])),
);

const ordered = computed(() => orderedMembers(props.route));
const unsequenced = computed(() => unsequencedMembers(props.route));

const editable = computed(
	() => props.route.state !== "completed" && props.route.state !== "cancelled",
);

// A discard confirmation belongs to the Route it was opened on: selecting
// another Route must not leave the second click armed over a different record,
// and a stale error would read as the new Route's.
watch(
	() => props.route.id,
	() => {
		discardPending.value = false;
		routeError.value = "";
	},
);

/** The technician's draft work no Route holds: the add-member door takes
 * exactly this set, since a Route binds one technician (§8 Rule 2). */
const addable = computed(() =>
	props.assignments.filter(
		(row) =>
			row.routeId === null &&
			row.technicianId === props.route.technicianId &&
			row.state === "draft",
	),
);

function assignmentLabel(assignment: SyncAssignment): string {
	return `#${assignment.id} — ${kindLabel(assignment.kind)} · ${targetLabel(assignment, props.sites)}`;
}

function memberLabel(assignmentId: number): string {
	const row = assignmentIndex.value.get(assignmentId);
	return row
		? `#${assignmentId} — ${kindLabel(row.kind)} · ${targetLabel(row, props.sites)}`
		: `#${assignmentId}`;
}

async function addMember() {
	if (addAssignmentId.value <= 0) return;
	busy.value = true;
	routeError.value = "";
	try {
		// A member joins unsequenced: position null is the normal membership
		// (decision 10), and the Route sequences it on the operator's order.
		await apiClient.JSONPut(`/api/route/${props.route.id}/assignment`, {
			assignment_id: addAssignmentId.value,
			position: null,
		});
		emit(
			"notify",
			"Added to route",
			`Assignment #${addAssignmentId.value} joined route #${props.route.id} unsequenced.`,
		);
		addAssignmentId.value = 0;
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not added", routeError.value);
	} finally {
		busy.value = false;
	}
}

async function removeMember(assignmentID: number) {
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONDelete(
			`/api/route/${props.route.id}/assignment/${assignmentID}`,
		);
		emit(
			"notify",
			"Removed from route",
			`Assignment #${assignmentID} left route #${props.route.id}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not removed", routeError.value);
	} finally {
		busy.value = false;
	}
}

async function writeOrder(assignmentIDs: number[]) {
	const body: RouteReorderRequest = { assignment_ids: assignmentIDs };
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut<SyncRoute>(
			`/api/route/${props.route.id}/order`,
			body,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route order not written", routeError.value);
	} finally {
		busy.value = false;
	}
}

// A drop states the route's whole sequence: the ordered members in their new
// order, then whatever is still unsequenced. The door assigns positions
// 0..n-1 to the list it is given, so a drop is also what sequences the tail.
async function dropAt(index: number) {
	const moving = dragId.value;
	dragId.value = null;
	if (moving === null) return;
	const rest = ordered.value
		.map((member) => member.assignmentId)
		.filter((assignmentID) => assignmentID !== moving);
	rest.splice(Math.min(index, rest.length), 0, moving);
	const unsequencedIDs = unsequenced.value
		.map((member) => member.assignmentId)
		.filter((assignmentID) => assignmentID !== moving);
	await writeOrder([...rest, ...unsequencedIDs]);
}

async function dispatchRoute() {
	const body: RouteStateUpdate = { to: "dispatched" };
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut<SyncRoute>(
			`/api/route/${props.route.id}/state`,
			body,
		);
		emit(
			"notify",
			"Route dispatched",
			`Route #${props.route.id} is on its way.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route not dispatched", routeError.value);
	} finally {
		busy.value = false;
	}
}

// Discarding is the draft's own action: the server releases the Route's members
// back to the unassigned pool and cancels the Route in one write, so the reload
// behind `changed` shows a cancelled Route holding nothing. It is the one route
// action with no way back, hence the confirm step above.
async function discardRoute() {
	const id = props.route.id;
	const members = props.route.members.length;
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPost<SyncRoute>(`/api/route/${id}/discard`, {});
		discardPending.value = false;
		emit(
			"notify",
			"Route discarded",
			`Route #${id} was discarded${
				members
					? `; ${members} assignment(s) returned to the unassigned pool`
					: ""
			}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Route not discarded", routeError.value);
	} finally {
		busy.value = false;
	}
}
</script>
